import { useState } from 'react';
import {
  FAIL_REASON_LABEL,
  GOODS_DISPOSITION_LABEL,
  TRANSITIONS,
  allowedActionsFor,
  isTerminal,
  ulid,
  type OrderAction,
  type OrderStatus,
} from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { patchCachedOrder } from '../lib/cached';
import type { DriverRow, OrderRow } from '../lib/types';

/**
 * 一次调用最多带多少单 —— 和 `BatchTransitionSchema` 里的上限一致。
 *
 * ★ 两边写死同一个数字是有意的:接口那边不能取消这个上限
 *   (一次几百单的事务会把库压住),而界面这边超了就分批发。
 *   哪天接口放宽了,这里跟着改;写成从接口读回来反而更容易不同步。
 */
const MAX_PER_CALL = 200;

/**
 * 批量改状态。
 *
 * ★ 用户原话:「不止可以批量取消,可以批量标记取货,送达/未送达等所有操作,
 *   批量转派司机」。
 *
 * ★ 能做哪些动作**从状态机表算出来**,不在这里另写一份清单。
 *   手写清单的话,加一个状态要改两个地方,漏一个就是
 *   「界面上能点、后端拒绝」——  而批量操作里这种错会一次发生几十遍。
 *
 * ★ 选中的单状态往往不一样(调度员框选了一屏)。所以按动作算出
 *   **这一批里有几单能做**,做不了的那几单明说,而不是
 *   闷头发过去等一半失败 —— 后端是逐单独立执行的,部分成功是正常结果,
 *   但人要在点之前就知道会是这样。
 */

const ACTION_LABEL: Partial<Record<OrderAction, string>> = {
  /*
   * ★ 转派和「派发」不是一回事,两个都要有。
   *
   *   「派发」(ASSIGN)只接 PENDING / ASSIGNED —— 还没人跑的单;
   *   转派(TRANSFER)接到 IN_TRANSIT,连**货已经在车上**的都能换人。
   *   用户要的「批量转派司机」是后者:司机请假、车坏了,
   *   他手上那十几单要整批换人,而那些单早就过了待派阶段。
   */
  TRANSFER: '转派给其他司机',
  /*
   * ★ 「待接单」的单原来在这张弹窗里一步都推不动 —— 只剩转派和取消。
   *   用户报的:「订单是待接单状态的时候,管理端批量操作只能转派或者取消,
   *   不能继续下一步标记已经取货等」。
   */
  ACCEPT: '代司机接单',
  PICKUP: '标记已取货',
  DELIVER: '标记已送达',
  FAIL: '标记未送达',
  RETRY: '重新投递',
  UNASSIGN: '撤回派单',
  RETURN: '标记退回',
  CANCEL: '取消订单',
};

/** 这些做完就回不去了,要红色 + 二次确认 */
const DANGER: OrderAction[] = ['CANCEL', 'RETURN'];

interface Result {
  results: Array<{
    orderId: string;
    status: string;
    newStatus?: string;
    error?: { message: string };
  }>;
  summary: { total: number; succeeded: number; failed: number };
}

export function BatchActionDialog({
  orders,
  drivers,
  permissions,
  onClose,
  onDone,
  onPatched,
}: {
  orders: OrderRow[];
  drivers: DriverRow[];
  permissions: string[];
  onClose: () => void;
  onDone: () => Promise<void>;
  /**
   * 哪几单变成了什么 —— 让列表**就地**改,不用整表重拉。
   *
   * ★ 逐单报:批量里部分成功是常态(后端是一单一单独立执行的),
   *   报「这一批都成了」会把失败那几单也画成新状态。
   */
  onPatched?: (changes: Array<{ id: string; status: string }>) => void;
}) {
  const [action, setAction] = useState<OrderAction | null>(null);
  const [reason, setReason] = useState('');
  const [failReason, setFailReason] = useState('NO_ONE_HOME');
  const [failDisposition, setFailDisposition] = useState('RETRY');
  const [disposition, setDisposition] = useState('RETURNED_TO_SENDER');
  const [confirmed, setConfirmed] = useState(false);
  /** 「这批里的特殊物品都交到收件人手上了」—— 见下面 needsHandover */
  const [handoverOk, setHandoverOk] = useState(false);
  const [driverId, setDriverId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  /*
   * ★ 标记取货时,「待接单」的单也算能做 —— 发之前先代司机接一下。
   *
   *   状态机里 PICKUP 只从 ACCEPTED 走(司机得先点确认),
   *   而后台面对的常常是「货已经拉走了,但司机一直没点接单」。
   *   让他先做一遍「代接单」再回来做一遍「取货」是两趟操作,
   *   而他要的是一件事:把这批单推到已取货。
   *
   *   所以这里把这几单一并算进去,submit 时自动多发一次 ACCEPT。
   *   时间轴上仍然是**两条**记录(代接单、取货)—— 发生过的事不合并。
   */
  const canAccept = permissions.includes(TRANSITIONS.ACCEPT.permission);
  const needsAcceptFirst = (o: OrderRow): boolean => canAccept && o.status === 'ASSIGNED';

  /** 这一批里,每个动作分别有几单能做 */
  const doable = (a: OrderAction): OrderRow[] =>
    orders.filter(
      (o) =>
        allowedActionsFor(o.status as OrderStatus, 'USER').includes(a) ||
        (a === 'PICKUP' && needsAcceptFirst(o)),
    );

  const actions = (Object.keys(ACTION_LABEL) as OrderAction[])
    .filter((a) => permissions.includes(TRANSITIONS[a].permission))
    .map((a) => ({ action: a, rows: doable(a) }))
    .filter((x) => x.rows.length > 0);

  const picked = action ? doable(action) : [];
  /*
   * ★ 取消时,**已经被司机领走的**要单独拎出来。
   *
   *   用户原话:「如果有已经被领取的弹出二次确认窗口」。
   *   已派下去的单被批量取消,司机那边是一条已经在跑的活突然消失 ——
   *   而调度员框选时多半没注意到这几单和别的不一样。
   */
  const taken = action === 'CANCEL' ? picked.filter((o) => o.driver != null) : [];
  const needsConfirm = taken.length > 0 || (action != null && DANGER.includes(action));

  const def = action ? TRANSITIONS[action] : null;
  const needsReason = def?.requiresReason ?? false;
  const needsFail = action === 'FAIL';
  const needsDriver = action === 'TRANSFER';
  // 已取货的单取消时要交代货物去向,否则后端 guard 会拒
  const needsDisposition = action === 'CANCEL' && picked.some((o) => o.pickedUpAt != null);

  /*
   * ★ 送达的另一道硬门槛:有特殊物品的单,后端要求**逐项**确认交出去了。
   *
   *   用户原话:「批量标记完成的时候有特殊物品就会标记失败,
   *   这种需要单独去更改管理端,其实可以批量操作的 ——
   *   可以在批量操作的时候弹出一个提示框说有冻品是否确认」。
   *
   *   逐项那个勾是给**站在门口的司机**用的(见后端 guards.ts:
   *   一个笼统的「都给了」表示不了「冻品给了、易碎那件忘在车上」)。
   *   而后台补记的时候没人站在门口,他能确认的就是「这些单都送完了」——
   *   所以这里是一个勾,但把有哪几样、各几单摆出来给他看。
   */
  const specialPicked =
    action === 'DELIVER' ? picked.filter((o) => (o.specialItems ?? []).length > 0) : [];
  const needsHandover = specialPicked.length > 0;
  /** 哪几样、各几单 —— 「冻品 3 单、易碎 1 单」 */
  const specialTally = (() => {
    const n = new Map<string, number>();
    for (const o of specialPicked) {
      for (const s of o.specialItems ?? []) n.set(s.label, (n.get(s.label) ?? 0) + 1);
    }
    return [...n.entries()].map(([label, count]) => `${label} ${count} 单`);
  })();

  /*
   * ★ 代收的单**不在这里放行**。
   *
   *   特殊物品那个勾确认的是「东西给出去了」,而代收确认的是「钱收到了」——
   *   一次点头替几十单认下几千块钱的到账,和确认一箱冻品交出去了不是一回事。
   *   所以这几单照旧一单一单点(详情里会让他填收款方式和金额)。
   *   但要在**点之前**说清楚,而不是让他在结果页上看到一排失败。
   */
  const codPicked =
    action === 'DELIVER'
      ? picked.filter((o) => Number(o.codAmount ?? 0) > 0 || o.codPending === true)
      : [];

  const ready =
    action != null &&
    picked.length > 0 &&
    (!needsReason || reason.trim().length > 0) &&
    (!needsDriver || driverId !== '') &&
    (!needsConfirm || confirmed) &&
    (!needsHandover || handoverOk);

  async function submit() {
    if (!ready || !action || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const batchKey = `batch-${ulid()}`;
      /*
       * ★ 按「这单有哪几样特殊物品」分组,每组发一次,payload 里带这一组那几样。
       *
       *   不把所有单合成一次、带上全部品类的并集 —— 那样一单只有冻品的单,
       *   时间轴上会写着「易碎品已交付」,而它根本没有易碎品。
       *   批量操作留下的记录也得对得上每一单的事实。
       *
       *   没有特殊物品的单归在一组(codes 为空),就是原来那一次调用。
       */
      const groups = new Map<string, { codes: string[]; ids: string[] }>();
      for (const o of picked) {
        const codes = needsHandover ? [...(o.specialItems ?? []).map((s) => s.code)].sort() : [];
        const key = codes.join(',');
        const g = groups.get(key) ?? { codes, ids: [] };
        g.ids.push(o.id);
        groups.set(key, g);
      }

      /*
       * ★ 先把「待接单」那几单代接一下 —— 不然它们在 PICKUP 那一步会被
       *   状态机拒掉(PICKUP 只从 ACCEPTED 走)。
       *   接不动的(比如已经被别人改过)就让它留在原状态,
       *   后面那一步会把它算成失败并逐条说明原因。
       */
      const toAccept = action === 'PICKUP' ? picked.filter(needsAcceptFirst) : [];
      if (toAccept.length > 0) {
        await api<Result>('/orders/batch-transition', {
          method: 'POST',
          body: {
            action: 'ACCEPT',
            orderIds: toAccept.map((o) => o.id),
            batchKey: `${batchKey}-accept`,
            reason: reason.trim() || '后台代司机接单 —— 接着标记取货',
          },
        }).catch(() => undefined);
      }

      const parts: Result[] = [];
      let chunkNo = 0;
      for (const g of groups.values()) {
        /*
         * ★ 一次最多 200 单(接口的上限),超了自己**分批发**。
         *
         *   不分的话:选了 256 单点取消,界面只说「请求参数校验失败」——
         *   而真正的原因(「一次最多 200 单」)躺在 details 里。
         *   人选 256 单是有意的,他要的是「把这些都取消掉」;
         *   让他自己数着 200 分两次点,是把接口的限制推给他。
         *
         * ★ 每批一个自己的 batchKey:服务端的逐单幂等键是
         *   `${batchKey}:${orderId}`,而各批的单互不重叠,所以分批安全;
         *   但 batchKey 相同时那几批在时间轴上会被当成同一次操作,
         *   查起来分不开,所以还是给每批一个后缀。
         */
        for (let i = 0; i < g.ids.length; i += MAX_PER_CALL) {
          const slice = g.ids.slice(i, i + MAX_PER_CALL);
          chunkNo++;
          parts.push(
            await api<Result>('/orders/batch-transition', {
              method: 'POST',
              body: {
                action,
                orderIds: slice,
                batchKey: chunkNo === 1 ? batchKey : `${batchKey}-${chunkNo}`,
                ...(needsDriver ? { driverId } : {}),
                ...(reason.trim() ? { reason: reason.trim() } : {}),
                payload: {
                  ...(needsFail ? { failReason, failDisposition } : {}),
                  ...(needsDisposition ? { goodsDisposition: disposition } : {}),
                  ...(g.codes.length > 0 ? { specialItemsHandedOver: g.codes } : {}),
                  /*
                   * ★ 批量代点同样受拍照门槛约束,这里不带 proofOverrideReason。
                   *   一次绕过几十单的凭证要求,比单张绕过危险得多 ——
                   *   真要那么做,就一单一单地在详情里做,那里会明确告诉他
                   *   「这一单会记成无照片凭证」。
                   */
                },
              },
            }),
          );
        }
      }
      const r: Result = {
        results: parts.flatMap((p) => p.results),
        summary: {
          total: parts.reduce((s, p) => s + p.summary.total, 0),
          succeeded: parts.reduce((s, p) => s + p.summary.succeeded, 0),
          failed: parts.reduce((s, p) => s + p.summary.failed, 0),
        },
      };
      setResult(r);
      const changed: Array<{ id: string; status: string }> = [];
      for (const one of r.results) {
        if (one.status !== 'SUCCESS' || !one.newStatus) continue;
        changed.push({ id: one.orderId, status: one.newStatus });
        void patchCachedOrder(
          one.orderId,
          { status: one.newStatus },
          isTerminal(one.newStatus as OrderStatus),
        );
      }
      onPatched?.(changed);
      // 全成了就直接关掉;有失败的话停在这儿,让他看清楚是哪几单
      if (r.summary.failed === 0) {
        await onDone();
        onClose();
      } else {
        await onDone();
      }
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '操作失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="批量操作"
        className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5
                   shadow-2xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          批量操作 · 选中 {orders.length} 单
        </h2>

        {result ? (
          <BatchResult result={result} onClose={onClose} />
        ) : (
          <>
            <p className="mt-1 text-sm text-slate-500">
              选中的单状态不一样时,只有能做这个动作的那几单会被处理 —— 下面写着各是几单
            </p>

            <div className="mt-3 space-y-2">
              {actions.length === 0 && (
                <div
                  className="rounded-lg bg-slate-50 px-3 py-4 text-center text-sm text-slate-500
                                dark:bg-slate-900/50"
                >
                  选中的这些单,当前状态下没有可以批量做的操作
                </div>
              )}
              {actions.map(({ action: a, rows }) => (
                <label
                  key={a}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                    action === a
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="batchAction"
                    checked={action === a}
                    onChange={() => {
                      setAction(a);
                      setConfirmed(false);
                    }}
                  />
                  <span
                    className={
                      DANGER.includes(a)
                        ? 'text-red-700 dark:text-red-400'
                        : 'text-slate-900 dark:text-slate-100'
                    }
                  >
                    {ACTION_LABEL[a]}
                  </span>
                  <span className="ml-auto text-xs text-slate-500">
                    {rows.length} / {orders.length} 单
                  </span>
                </label>
              ))}
            </div>

            {needsDriver && (
              <label className="mt-3 block">
                <span className="mb-1 block text-xs text-slate-500">转给哪个司机</span>
                <select
                  aria-label="转给哪个司机"
                  value={driverId}
                  onChange={(e) => setDriverId(e.target.value)}
                  className={SELECT}
                >
                  <option value="">选择司机…</option>
                  {drivers
                    .filter((d) => d.status === 'ACTIVE')
                    .map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}({d.driverNo})· 手上 {d.currentOrderCount} 单
                      </option>
                    ))}
                </select>
              </label>
            )}

            {needsFail && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs text-slate-500">没送到的原因</span>
                  <select
                    aria-label="没送到的原因"
                    value={failReason}
                    onChange={(e) => setFailReason(e.target.value)}
                    className={SELECT}
                  >
                    {Object.entries(FAIL_REASON_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs text-slate-500">货怎么处理</span>
                  <select
                    aria-label="货怎么处理"
                    value={failDisposition}
                    onChange={(e) => setFailDisposition(e.target.value)}
                    className={SELECT}
                  >
                    <option value="RETRY">留在车上,改天再送</option>
                    <option value="RETURN_TO_SENDER">退回发货方</option>
                  </select>
                </label>
              </div>
            )}

            {needsDisposition && (
              <label className="mt-3 block">
                <span className="mb-1 block text-xs text-slate-500">
                  货物去向(这批里有已取货的单,必须交代)
                </span>
                <select
                  aria-label="货物去向"
                  value={disposition}
                  onChange={(e) => setDisposition(e.target.value)}
                  className={SELECT}
                >
                  {Object.entries(GOODS_DISPOSITION_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {needsReason && (
              <label className="mt-3 block">
                <span className="mb-1 block text-xs text-slate-500">
                  原因(必填,会记入每一单的时间轴)
                </span>
                <textarea
                  aria-label="原因"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  className={`${SELECT} h-auto py-2`}
                />
              </label>
            )}

            {/*
              ★ 会多做一步的话,要在点之前说出来。
                「代司机接单」是一条会进时间轴的记录,不该悄悄发生。
            */}
            {action === 'PICKUP' && picked.some(needsAcceptFirst) && (
              <div
                aria-label="会先代接单"
                className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm
                           text-slate-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300"
              >
                其中 <b>{picked.filter(needsAcceptFirst).length} 单还停在「待接单」</b> ——
                会先代司机接一下再标记取货,两步都会记进时间轴。
              </div>
            )}

            {/*
              ★ 有特殊物品的单:勾一下就能一起标记送达。
                不勾的话后端会逐单拒掉(「这一单有特殊物品还没确认交给收件人」),
                而人要为这几单一单一单地进详情 —— 用户报的就是这个。
            */}
            {needsHandover && (
              <label
                aria-label="确认特殊物品已交付"
                className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300
                           bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-950/40"
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={handoverOk}
                  onChange={(e) => setHandoverOk(e.target.checked)}
                />
                <span className="text-amber-900 dark:text-amber-200">
                  这批里有 <b>{specialPicked.length} 单带特殊物品</b>({specialTally.join('、')}) ——
                  勾上表示这些东西都已经交到收件人手上,会一并记进每一单的时间轴。
                </span>
              </label>
            )}

            {/*
              ★ 代收的单点之前就说清楚,而不是让他在结果页看到一排失败。
                钱不能靠一次点头替几十单认下来 —— 理由见上面 codPicked 那段。
            */}
            {codPicked.length > 0 && (
              <div
                aria-label="代收提示"
                className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm
                           text-slate-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300"
              >
                这批里有 <b>{codPicked.length} 单要代收货款</b> —— 这几单会失败,
                要一单一单进详情点送达(那里要填收了多少、怎么收的)。
                钱不能靠一次点头替几十单认下来。
              </div>
            )}

            {/*
              ★ 二次确认:已经被司机领走的单要单独点出来。
                调度员框选一屏的时候不会注意到这几单和别的不一样,
                而对司机来说是「一条正在跑的活突然消失了」。
            */}
            {needsConfirm && (
              <label
                aria-label="二次确认"
                className="mt-3 flex items-start gap-2 rounded-lg border border-red-300
                           bg-red-50 px-3 py-2 text-sm dark:border-red-800 dark:bg-red-950/40"
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                <span className="text-red-800 dark:text-red-300">
                  {taken.length > 0 ? (
                    <>
                      这 {picked.length} 单里有 <b>{taken.length} 单已经派给司机</b>
                      {taken.length <= 3 && (
                        <>({[...new Set(taken.map((o) => o.driver?.name))].join('、')})</>
                      )}
                      —— 取消之后,他们手上正在跑的活会直接消失。确认要这么做。
                    </>
                  ) : (
                    <>这个动作做完回不去了,确认对这 {picked.length} 单执行。</>
                  )}
                </span>
              </label>
            )}

            {err && (
              <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-slate-500">
                取消
              </button>
              <button
                onClick={() => void submit()}
                disabled={!ready || busy}
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-40 ${
                  action && DANGER.includes(action) ? 'bg-red-600' : 'bg-brand-600'
                }`}
              >
                {busy ? '处理中…' : `对 ${picked.length} 单执行`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * 结果。
 *
 * ★ 失败的那几单要**逐条**说明原因,不是只给一个数字。
 *   批量操作里「12 成功 3 失败」这种汇总等于没说 ——
 *   人接下来要做的事完全取决于那 3 单为什么失败。
 */
function BatchResult({ result, onClose }: { result: Result; onClose: () => void }) {
  const failed = result.results.filter((r) => r.status !== 'SUCCESS');
  return (
    <>
      <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-900/50">
        成功 {result.summary.succeeded} 单
        {result.summary.failed > 0 && ` · 失败 ${result.summary.failed} 单`}
      </div>

      {failed.length > 0 && (
        <ul className="mt-3 max-h-60 space-y-1 overflow-y-auto text-sm">
          {failed.map((f) => (
            <li
              key={f.orderId}
              className="rounded-lg border border-red-200 px-3 py-1.5 dark:border-red-900"
            >
              <span className="font-mono text-xs text-slate-500">{f.orderId.slice(-8)}</span>
              <span className="ml-2 text-red-700 dark:text-red-400">
                {f.error?.message ?? '未知原因'}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex justify-end">
        <button
          onClick={onClose}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white"
        >
          知道了
        </button>
      </div>
    </>
  );
}

const SELECT =
  'w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm';
